import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  FiChevronDown, FiChevronRight, FiCalendar,
  FiAlertOctagon, FiAward, FiBookOpen, FiCloud, FiDollarSign, FiFileText, FiRss,
  FiExternalLink, FiMic, FiClock, FiBarChart2, FiTarget, FiRotateCcw,
  FiCheckSquare, FiHelpCircle, FiZap,
} from 'react-icons/fi'
import { cn } from '../../lib/utils'
import Card from '../../components/ui/Card'
import BriefCardView from './BriefCard'
import CourtRulingCard from './CourtRulingCard'
import {
  KoshaGuideSection, NewsArticlesSection,
  SubsidyAlertsSection, WeatherAlertWidget, LawChangesSection,
} from './ExtraSections'
import type { DayBriefBundle } from '../../lib/dailyBriefApi'

interface Props {
  bundle: DayBriefBundle
  defaultOpen?: boolean
  hideHeader?: boolean
}

type SectionColor = 'orange' | 'purple' | 'blue' | 'emerald' | 'cyan' | 'rose' | 'amber'

const COLOR_CLASSES: Record<SectionColor, {
  wrap: string
  iconBox: string
  title: string
  badge: string
}> = {
  orange:  { wrap: 'bg-orange-500/[0.03] dark:bg-orange-500/[0.01] border-l-4 border-orange-500',  iconBox: 'bg-orange-500/20  text-orange-600  dark:text-orange-300',  title: 'text-orange-700  dark:text-orange-200',  badge: 'bg-orange-500/20  text-orange-700  dark:text-orange-300' },
  purple:  { wrap: 'bg-purple-500/[0.03] dark:bg-purple-500/[0.01] border-l-4 border-purple-500',  iconBox: 'bg-purple-500/20  text-purple-600  dark:text-purple-300',  title: 'text-purple-700  dark:text-purple-200',  badge: 'bg-purple-500/20  text-purple-700  dark:text-purple-300' },
  blue:    { wrap: 'bg-blue-500/[0.03] dark:bg-blue-500/[0.01]   border-l-4 border-blue-500',    iconBox: 'bg-blue-500/20    text-blue-600    dark:text-blue-300',    title: 'text-blue-700    dark:text-blue-200',    badge: 'bg-blue-500/20    text-blue-700    dark:text-blue-300' },
  emerald: { wrap: 'bg-emerald-500/[0.03] dark:bg-emerald-500/[0.01] border-l-4 border-emerald-500', iconBox: 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-300', title: 'text-emerald-700 dark:text-emerald-200', badge: 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300' },
  cyan:    { wrap: 'bg-cyan-500/[0.03] dark:bg-cyan-500/[0.01]    border-l-4 border-cyan-500',    iconBox: 'bg-cyan-500/20    text-cyan-600    dark:text-cyan-300',    title: 'text-cyan-700    dark:text-cyan-200',    badge: 'bg-cyan-500/20    text-cyan-700    dark:text-cyan-300' },
  rose:    { wrap: 'bg-rose-500/[0.03] dark:bg-rose-500/[0.01]    border-l-4 border-rose-500',    iconBox: 'bg-rose-500/20    text-rose-600    dark:text-rose-300',    title: 'text-rose-700    dark:text-rose-200',    badge: 'bg-rose-500/20    text-rose-700    dark:text-rose-300' },
  amber:   { wrap: 'bg-amber-500/[0.03] dark:bg-amber-500/[0.01]   border-l-4 border-amber-500',   iconBox: 'bg-amber-500/20   text-amber-600   dark:text-amber-300',   title: 'text-amber-700   dark:text-amber-200',   badge: 'bg-amber-500/20   text-amber-700   dark:text-amber-300' },
}

interface CollapsibleSectionProps {
  icon: React.ReactNode
  title: string
  count?: number
  color: SectionColor
  description?: string
  children: React.ReactNode
  defaultOpen?: boolean
}

function CollapsibleSection({
  icon,
  title,
  count,
  color,
  description,
  children,
  defaultOpen = true,
}: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const c = COLOR_CLASSES[color]

  return (
    <Card className={cn("overflow-hidden transition-all duration-300 border-l-4 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-brand-primary/[0.02]", c.wrap)} hover>
      <button
        type="button"
        onClick={() => setIsOpen(v => !v)}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 hover:bg-surface-elevated/20 transition-all text-left"
      >
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-none ${c.iconBox} shadow-sm border border-brand-primary/5`}>
            {icon}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2.5">
              <h3 className="text-base font-bold text-ink-primary tracking-tight">{title}</h3>
              {count !== undefined && (
                <span className={`text-xs px-2 py-0.5 rounded-full font-bold shadow-sm ${c.badge}`}>
                  {count}건
                </span>
              )}
            </div>
            {description && (
              <p className="text-[11px] text-ink-secondary mt-0.5 font-normal">{description}</p>
            )}
          </div>
        </div>
        <div className="flex-none text-ink-secondary pr-1">
          {isOpen ? <FiChevronDown size={18} /> : <FiChevronRight size={18} />}
        </div>
      </button>

      {isOpen && (
        <div className="border-t border-surface-elevated/40 px-5 py-5 space-y-6 bg-surface/30">
          {children}
        </div>
      )}
    </Card>
  )
}

function CompactMediaCard({
  videoId, youtubeUrl, podcastUrl, podcastDurLabel,
  audioRef, onPlay, onLoadedMetadata, onTimeUpdate,
}: {
  videoId: string | null
  youtubeUrl: string | null
  podcastUrl: string | null
  podcastDurLabel: string | null
  audioRef: React.RefObject<HTMLAudioElement | null>
  onPlay: () => void
  onLoadedMetadata: () => void
  onTimeUpdate: () => void
}) {
  const [videoOpen, setVideoOpen] = useState(false)
  const thumb = videoId ? `https://i.ytimg.com/vi/${videoId}/mqdefault.jpg` : null

  return (
    <div className="rounded-xl overflow-hidden bg-gradient-to-br from-slate-900 to-slate-800 border border-white/[0.08] shadow">
      {/* 영상 — 접힘 기본, 클릭 시 임베드 펼침 */}
      {videoId && !videoOpen && (
        <button
          type="button"
          onClick={() => setVideoOpen(true)}
          className="group w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/[0.04] transition border-b border-white/[0.06]"
          aria-label="오늘의 영상 재생"
        >
          <div className="relative w-20 h-12 sm:w-28 sm:h-16 rounded-md overflow-hidden flex-none bg-slate-950">
            {thumb && <img src={thumb} alt="" className="w-full h-full object-cover" loading="lazy" />}
            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-red-500/95 group-hover:bg-red-500 flex items-center justify-center shadow-lg">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="white" aria-hidden><path d="M8 5v14l11-7z"/></svg>
              </div>
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] tracking-wider font-bold text-red-300 uppercase">오늘의 영상</div>
            <div className="text-sm font-bold text-white truncate">클릭해서 사이트에서 바로 재생</div>
          </div>
          {youtubeUrl && (
            <a
              href={youtubeUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              className="hidden sm:inline-flex items-center text-[11px] text-slate-400 hover:text-slate-200 transition"
            >
              YouTube →
            </a>
          )}
        </button>
      )}
      {videoId && videoOpen && (
        <div className="border-b border-white/[0.06] p-3">
          <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1&autoplay=1`}
              title="오늘의 안전 브리핑 영상"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full rounded-md border border-white/10"
              loading="lazy"
            />
          </div>
          <div className="flex items-center justify-between mt-2">
            <button onClick={() => setVideoOpen(false)} className="text-[11px] text-slate-400 hover:text-slate-200">
              ▲ 영상 접기
            </button>
            {youtubeUrl && (
              <a href={youtubeUrl} target="_blank" rel="noopener noreferrer"
                 className="text-[11px] text-red-300 hover:text-red-200">YouTube 에서 열기 →</a>
            )}
          </div>
        </div>
      )}

      {/* 팟캐스트 — 컴팩트 한 줄 */}
      {podcastUrl && (
        <div className="flex items-center gap-3 px-4 py-3">
          <div className="w-9 h-9 rounded-lg bg-cyan-500/15 text-cyan-300 flex items-center justify-center flex-none">
            <FiMic size={16} />
          </div>
          <div className="min-w-0 flex-none">
            <div className="text-[10px] tracking-wider font-bold text-cyan-300 uppercase leading-none">팟캐스트</div>
            {podcastDurLabel && (
              <div className="text-[11px] text-slate-400 mt-0.5">약 {podcastDurLabel}</div>
            )}
          </div>
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <audio
            ref={audioRef}
            controls
            controlsList="nodownload noremoteplayback noplaybackrate"
            preload="none"
            src={podcastUrl}
            className="flex-1 min-w-0 h-9"
            style={{ maxHeight: '36px' }}
            onPlay={onPlay}
            onLoadedMetadata={onLoadedMetadata}
            onTimeUpdate={onTimeUpdate}
            onContextMenu={e => e.preventDefault()}
          >
            브라우저가 오디오 재생을 지원하지 않습니다.
          </audio>
        </div>
      )}
    </div>
  )
}

function QuizOptions({ options, answerIndex, explanation }: { options: string[]; answerIndex: number; explanation?: string }) {
  const [picked, setPicked] = useState<number | null>(null)
  const marks = ['①', '②', '③', '④']
  return (
    <div>
      <div className="space-y-2">
        {options.map((o, i) => {
          const isPicked = picked === i
          const isCorrect = i === answerIndex
          const revealed = picked !== null
          return (
            <button
              key={i}
              type="button"
              onClick={() => setPicked(i)}
              disabled={revealed}
              className={`w-full text-left flex items-start gap-3 rounded-lg border px-3.5 py-2.5 text-sm transition-all ${
                !revealed
                  ? 'border-surface-border hover:border-cyan-500/60 hover:bg-cyan-500/[0.03] cursor-pointer'
                  : isCorrect
                    ? 'border-emerald-500/60 bg-emerald-500/[0.06]'
                    : isPicked
                      ? 'border-red-500/60 bg-red-500/[0.06]'
                      : 'border-surface-border opacity-60'
              }`}
            >
              <span className={`font-serif italic font-bold ${revealed && isCorrect ? 'text-emerald-600 dark:text-emerald-400' : revealed && isPicked ? 'text-red-500' : 'text-cyan-700 dark:text-cyan-300'}`}>{marks[i]}</span>
              <span className="text-ink-primary leading-relaxed">{o}</span>
              {revealed && isCorrect && <span className="ml-auto text-emerald-600 dark:text-emerald-400 text-xs font-bold">정답</span>}
              {revealed && isPicked && !isCorrect && <span className="ml-auto text-red-500 text-xs font-bold">오답</span>}
            </button>
          )
        })}
      </div>
      {picked !== null && explanation && (
        <div className="mt-4 px-4 py-3 rounded-lg bg-cyan-500/[0.06] border border-cyan-500/20">
          <div className="text-[10px] tracking-[0.18em] font-bold text-cyan-700 dark:text-cyan-300 uppercase mb-1.5">해설</div>
          <p className="text-sm text-ink-primary leading-relaxed">{explanation}</p>
        </div>
      )}
    </div>
  )
}

export default function DayBriefCard({ bundle, defaultOpen = false, hideHeader = false }: Props) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(defaultOpen)
  const totalItems =
    bundle.briefs.length +
    bundle.court_rulings.length +
    bundle.kosha_guides.length +
    bundle.law_changes.length +
    (bundle.safety_news?.length ?? 0) +
    bundle.weather_alerts.length +
    bundle.subsidy_alerts.length
  const isEmpty = totalItems === 0
  const dateLabel = bundle.is_today
    ? t('dailyBrief.day.today', { defaultValue: '오늘' })
    : (() => {
        const d = new Date(bundle.date + 'T00:00:00')
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        const diff = Math.round((today.getTime() - d.getTime()) / 86400000)
        if (diff === 1) return t('dailyBrief.day.yesterday', { defaultValue: '어제' })
        return bundle.date
      })()

  const podcastDur = bundle.podcast_duration
    ? `${Math.floor(bundle.podcast_duration / 60)}분 ${Math.round(bundle.podcast_duration % 60)}초`
    : null

  // 잠금화면/백그라운드 재생 — Media Session API (화면 꺼도 재생 + 잠금화면 제어)
  const audioRef = useRef<HTMLAudioElement>(null)
  const setupMediaSession = () => {
    const a = audioRef.current
    if (!a || !('mediaSession' in navigator)) return
    const artwork = bundle.safety_news_image
      ? [{ src: bundle.safety_news_image, sizes: '1024x576', type: 'image/png' }]
      : []
    // eslint-disable-next-line no-undef
    navigator.mediaSession.metadata = new MediaMetadata({
      title: '오늘의 안전 브리핑',
      artist: 'Heinrich 안전 팟캐스트',
      album: `${bundle.date} 일일 브리핑`,
      artwork,
    })
    navigator.mediaSession.setActionHandler('play', () => { void a.play() })
    navigator.mediaSession.setActionHandler('pause', () => a.pause())
    navigator.mediaSession.setActionHandler('seekbackward', (d) => {
      a.currentTime = Math.max(0, a.currentTime - (d.seekOffset || 10))
    })
    navigator.mediaSession.setActionHandler('seekforward', (d) => {
      a.currentTime = Math.min(a.duration || a.currentTime, a.currentTime + (d.seekOffset || 10))
    })
    navigator.mediaSession.setActionHandler('seekto', (d) => {
      if (d.seekTime != null) a.currentTime = d.seekTime
    })
  }
  const updatePositionState = () => {
    const a = audioRef.current
    if (!a || !('mediaSession' in navigator) || !navigator.mediaSession.setPositionState) return
    if (a.duration && isFinite(a.duration)) {
      navigator.mediaSession.setPositionState({
        duration: a.duration,
        playbackRate: a.playbackRate,
        position: Math.min(a.currentTime, a.duration),
      })
    }
  }

  const content = (
    <div className="space-y-6">
      {isEmpty && (
        <div className="py-12 text-center text-ink-faint text-sm">
          {t('dailyBrief.day.noData', { defaultValue: '이날은 등록된 콘텐츠가 없습니다.' })}
        </div>
      )}

      {/* TL;DR 30초 요약 — 비collapsible 상단 카드 */}
      {(bundle.tldr?.length ?? 0) > 0 && (
        <div className="rounded-2xl border border-brand-primary/20 bg-gradient-to-br from-brand-primary/[0.04] to-transparent px-5 py-4 sm:px-6 sm:py-5">
          <div className="flex items-center gap-2 mb-3">
            <FiClock size={14} className="text-brand-primary" />
            <span className="text-[10px] tracking-[0.18em] font-bold text-brand-primary uppercase">In Brief · 30초 요약</span>
          </div>
          <ul className="space-y-1.5">
            {bundle.tldr!.map((l, i) => (
              <li key={i} className="text-sm text-ink-primary leading-relaxed pl-4 relative">
                <span className="absolute left-0 top-2 w-1 h-1 rounded-full bg-brand-primary" />
                {l}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* 0. 오늘의 미디어 — 영상(접힘) + 팟캐스트(콤팩트). 뉴스 등 아래 섹션 인지 방해 안 되게 슬림 */}
      {(bundle.youtube_video_id || bundle.podcast_url) && (
        <CompactMediaCard
          videoId={bundle.youtube_video_id || null}
          youtubeUrl={bundle.youtube_url || null}
          podcastUrl={bundle.podcast_url || null}
          podcastDurLabel={podcastDur}
          audioRef={audioRef}
          onPlay={setupMediaSession}
          onLoadedMetadata={updatePositionState}
          onTimeUpdate={updatePositionState}
        />
      )}

      {/* Section 01 · 사고 사례 (최우선) */}
      {bundle.briefs.length > 0 && (
        <CollapsibleSection
          icon={<FiAlertOctagon size={20} />}
          title="오늘의 사고 사례"
          count={bundle.briefs.length}
          color="orange"
          description="행정안전부 일일상황 · AI 분석 + 4컷 예방 안내"
          defaultOpen={defaultOpen}
        >
          <div className="space-y-4">
            {bundle.briefs.map(b => <BriefCardView key={b.id} brief={b} />)}
          </div>
        </CollapsibleSection>
      )}

      {/* Section 02 · 날씨 */}
      {bundle.weather_alerts.length > 0 && (
        <CollapsibleSection
          icon={<FiCloud size={20} />}
          title="옥외 작업 영향 알림"
          count={bundle.weather_alerts.length}
          color="amber"
          description="폭염/한파/미세먼지 · 출근·작업 전 점검"
          defaultOpen={defaultOpen}
        >
          <WeatherAlertWidget alerts={bundle.weather_alerts} />
        </CollapsibleSection>
      )}

      {/* 2-2. 이번 주 사고 통계 */}
      {bundle.stats && (bundle.stats.this_week_count ?? 0) >= 0 && (
        <CollapsibleSection
          icon={<FiBarChart2 size={20} />}
          title="이번 주 산업재해 통계"
          color="rose"
          description={`${bundle.stats.week_start ?? ''} ~ ${bundle.stats.week_end ?? ''} · 행정안전부 적재 기준`}
          defaultOpen={defaultOpen}
        >
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="text-center py-3 border-r border-surface-elevated/40">
              <div className="font-serif text-5xl font-bold text-ink-primary leading-none">{bundle.stats.this_week_count ?? 0}</div>
              <div className="text-[10px] tracking-[0.18em] font-bold text-ink-secondary uppercase mt-2.5">사고 / 이번 주</div>
              {bundle.stats.delta_pct != null && (
                <div className={`text-xs italic mt-1.5 ${bundle.stats.delta_pct > 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                  {bundle.stats.delta_pct > 0 ? '↑ 전주 +' : '↓ 전주 '}{bundle.stats.delta_pct}%
                </div>
              )}
            </div>
            <div className="text-center py-3">
              <div className="font-serif text-5xl font-bold text-ink-primary leading-none">{bundle.stats.this_week_fatalities ?? 0}</div>
              <div className="text-[10px] tracking-[0.18em] font-bold text-ink-secondary uppercase mt-2.5">사망 / 이번 주</div>
              <div className="text-xs italic text-ink-faint mt-1.5">전주 {bundle.stats.prev_week_fatalities ?? 0}명</div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 pt-3 border-t border-surface-elevated/30">
            {(bundle.stats.top_types?.length ?? 0) > 0 && (
              <div>
                <div className="text-[10px] tracking-[0.18em] font-bold text-ink-faint uppercase mb-2">사고 유형 Top {bundle.stats.top_types!.length}</div>
                {bundle.stats.top_types!.map((t, i) => (
                  <div key={i} className="flex justify-between text-sm py-1.5 border-b border-surface-elevated/40 last:border-b-0">
                    <span className="text-ink-primary"><span className="font-serif italic text-ink-faint mr-2">{String(i+1).padStart(2,'0')}</span>{t.label}</span>
                    <span className="text-ink-faint text-xs">{t.count}건</span>
                  </div>
                ))}
              </div>
            )}
            {(bundle.stats.top_industries?.length ?? 0) > 0 && (
              <div>
                <div className="text-[10px] tracking-[0.18em] font-bold text-ink-faint uppercase mb-2">업종 Top {bundle.stats.top_industries!.length}</div>
                {bundle.stats.top_industries!.map((t, i) => (
                  <div key={i} className="flex justify-between text-sm py-1.5 border-b border-surface-elevated/40 last:border-b-0">
                    <span className="text-ink-primary"><span className="font-serif italic text-ink-faint mr-2">{String(i+1).padStart(2,'0')}</span>{t.label}</span>
                    <span className="text-ink-faint text-xs">{t.count}건</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CollapsibleSection>
      )}

      {/* Section 04 · 판례 */}
      {bundle.court_rulings.length > 0 && (
        <CollapsibleSection
          icon={<FiAward size={20} />}
          title="어제 선고된 산업안전 판결"
          count={bundle.court_rulings.length}
          color="purple"
          description="중대재해처벌법 · 산재보험 · 산업안전보건법"
          defaultOpen={defaultOpen}
        >
          <div className="space-y-3">
            {bundle.court_rulings.map(r => <CourtRulingCard key={r.id} ruling={r} />)}
          </div>
        </CollapsibleSection>
      )}

      {/* Section 05 · 법령 */}
      {bundle.law_changes.length > 0 && (
        <CollapsibleSection
          icon={<FiFileText size={20} />}
          title="법령 개정 알림"
          count={bundle.law_changes.length}
          color="blue"
          description="산업안전 관련 신규/개정/폐지"
          defaultOpen={defaultOpen}
        >
          <LawChangesSection changes={bundle.law_changes} />
        </CollapsibleSection>
      )}

      {/* Section 06 · KOSHA Guide */}
      {bundle.kosha_guides.length > 0 && (
        <CollapsibleSection
          icon={<FiBookOpen size={20} />}
          title="신규 안전보건 기술지침"
          count={bundle.kosha_guides.length}
          color="emerald"
          description="KOSHA Guide + 고용노동부 고시·예규"
          defaultOpen={defaultOpen}
        >
          <KoshaGuideSection guides={bundle.kosha_guides} />
        </CollapsibleSection>
      )}

      {/* Section 07 · 체크리스트 */}
      {bundle.checklist?.items && bundle.checklist.items.length > 0 && (
        <CollapsibleSection
          icon={<FiCheckSquare size={20} />}
          title={bundle.checklist.title || '오늘의 안전 체크리스트'}
          count={bundle.checklist.items.length}
          color="emerald"
          description="작업 시작 전 점검 항목"
          defaultOpen={defaultOpen}
        >
          <ul className="space-y-2">
            {bundle.checklist.items.map((it, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-ink-primary leading-relaxed">
                <FiCheckSquare size={15} className="text-emerald-600 dark:text-emerald-400 flex-none mt-0.5" />
                <span>{it}</span>
              </li>
            ))}
          </ul>
        </CollapsibleSection>
      )}

      {/* Section 08 · Q&A */}
      {bundle.qa?.question && (
        <CollapsibleSection
          icon={<FiHelpCircle size={20} />}
          title="현장에서 자주 묻는 질문"
          color="blue"
          description="안전관리자 Q&A"
          defaultOpen={defaultOpen}
        >
          <div className="mb-4">
            <span className="font-serif italic text-blue-700 dark:text-blue-300 font-bold mr-2">Q.</span>
            <span className="text-base font-bold text-ink-primary leading-relaxed">{bundle.qa.question}</span>
          </div>
          <div className="pl-6 border-l-2 border-blue-500/30">
            <span className="font-serif italic text-blue-700 dark:text-blue-300 font-bold mr-2">A.</span>
            <span className="text-sm text-ink-primary leading-relaxed">{bundle.qa.answer}</span>
          </div>
        </CollapsibleSection>
      )}

      {/* Section 09 · 퀴즈 */}
      {bundle.quiz?.question && (
        <CollapsibleSection
          icon={<FiZap size={20} />}
          title="오늘의 안전 퀴즈"
          color="cyan"
          description="객관식 1문항"
          defaultOpen={defaultOpen}
        >
          <p className="text-base font-bold text-ink-primary leading-relaxed mb-4">{bundle.quiz.question}</p>
          <QuizOptions options={bundle.quiz.options || []} answerIndex={bundle.quiz.answer_index ?? 0} explanation={bundle.quiz.explanation} />
        </CollapsibleSection>
      )}

      {/* Section 10 · 주요 산업안전 뉴스 */}
      {(bundle.safety_news?.length ?? 0) > 0 && (
        <CollapsibleSection
          icon={<FiRss size={20} />}
          title="주요 산업안전 뉴스"
          count={bundle.safety_news!.length}
          color="cyan"
          description="전날 발행된 국내 뉴스 — 정책·동향"
          defaultOpen={defaultOpen}
        >
          {bundle.safety_news_image && (
            <div className="relative rounded-xl overflow-hidden border border-cyan-500/20 shadow-sm mb-3">
              <img
                src={bundle.safety_news_image}
                alt="오늘의 산업안전 뉴스"
                className="w-full h-auto block"
                loading="lazy"
                onError={(e) => { (e.currentTarget.parentElement as HTMLElement).style.display = 'none' }}
              />
            </div>
          )}
          <div className="space-y-2.5">
            {bundle.safety_news!.map((n, i) => (
              <a
                key={i}
                href={n.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative flex gap-3.5 rounded-xl border border-surface-border bg-surface px-4 py-3.5 hover:border-cyan-500/60 hover:shadow-md hover:shadow-cyan-500/[0.06] hover:-translate-y-0.5 transition-all"
              >
                <div className="flex-none w-7 h-7 rounded-lg bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 flex items-center justify-center text-xs font-extrabold tabular-nums mt-0.5">
                  {String(i + 1).padStart(2, '0')}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-ink-primary leading-snug group-hover:text-cyan-700 dark:group-hover:text-cyan-300 transition-colors">
                    {n.headline}
                    <FiExternalLink size={12} className="inline-block ml-1.5 mb-0.5 text-ink-faint group-hover:text-cyan-500 transition-colors" />
                  </div>
                  {n.summary && (
                    <p className="text-xs text-ink-secondary leading-relaxed mt-1">{n.summary}</p>
                  )}
                  {n.publisher && (
                    <div className="inline-flex items-center gap-1 text-[11px] text-ink-faint mt-2 px-1.5 py-0.5 rounded bg-surface-elevated/50">
                      <FiRss size={9} className="text-cyan-500" />
                      {n.publisher}
                    </div>
                  )}
                </div>
              </a>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* Section 11 · 이달의 캠페인 */}
      {bundle.campaign?.title && (
        <CollapsibleSection
          icon={<FiTarget size={20} />}
          title={bundle.campaign.title}
          color="amber"
          description={bundle.campaign.theme || '이달의 안전 캠페인'}
          defaultOpen={defaultOpen}
        >
          {(bundle.campaign.points?.length ?? 0) > 0 && (
            <ul className="space-y-2">
              {bundle.campaign.points!.map((p, i) => (
                <li key={i} className="text-sm text-ink-primary leading-relaxed pl-5 relative">
                  <span className="absolute left-0 top-2 w-1.5 h-1.5 rounded-full bg-amber-500" />{p}
                </li>
              ))}
            </ul>
          )}
        </CollapsibleSection>
      )}

      {/* Section 12 · 니어미스 */}
      {bundle.near_miss?.situation && (
        <CollapsibleSection
          icon={<FiRotateCcw size={20} />}
          title="이번 주 아차사고 (Near-miss)"
          color="purple"
          description="과거 사고에서 배우는 익명 학습"
          defaultOpen={defaultOpen}
        >
          <div className="border-l-4 border-purple-500/60 pl-4 italic text-ink-primary text-base leading-relaxed mb-4">
            "{bundle.near_miss.situation}"
          </div>
          {bundle.near_miss.lesson && (
            <div className="mb-3">
              <div className="text-[10px] tracking-[0.18em] font-bold text-purple-700 dark:text-purple-300 uppercase mb-1">교훈</div>
              <p className="text-sm text-ink-primary leading-relaxed">{bundle.near_miss.lesson}</p>
            </div>
          )}
          {bundle.near_miss.action && (
            <div>
              <div className="text-[10px] tracking-[0.18em] font-bold text-emerald-700 dark:text-emerald-300 uppercase mb-1">오늘 현장에서 할 행동</div>
              <p className="text-sm text-emerald-700 dark:text-emerald-300 leading-relaxed font-medium">{bundle.near_miss.action}</p>
            </div>
          )}
        </CollapsibleSection>
      )}

      {/* Section 13 · 지원금 (맨 아래) */}
      {bundle.subsidy_alerts.length > 0 && (
        <CollapsibleSection
          icon={<FiDollarSign size={20} />}
          title="정부 보조금 / 지원사업"
          count={bundle.subsidy_alerts.length}
          color="rose"
          description="KOSHA · 고용노동부 · 기업마당 — 마감 임박순"
          defaultOpen={defaultOpen}
        >
          <SubsidyAlertsSection subsidies={bundle.subsidy_alerts} />
        </CollapsibleSection>
      )}
    </div>
  )

  if (hideHeader) {
    return <div className="space-y-6">{content}</div>
  }

  return (
    <Card>
      {/* 헤더 — 일자 + 카테고리별 카운트 뱃지 */}
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 hover:bg-surface-elevated/30 transition-colors text-left"
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {open ? <FiChevronDown size={18} className="text-ink-secondary flex-none" />
                : <FiChevronRight size={18} className="text-ink-secondary flex-none" />}
          <FiCalendar size={16} className="text-brand-primary flex-none" />
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-base font-bold text-ink-primary">{dateLabel}</span>
            <span className="text-sm text-ink-secondary">
              {bundle.date} · {bundle.weekday}요일
            </span>
            {bundle.is_today && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-brand-primary text-white font-semibold">
                TODAY
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-none flex-wrap justify-end">
          {bundle.briefs.length > 0 && <span className="px-1.5 py-0.5 rounded bg-orange-500/15 text-orange-700 dark:text-orange-300 text-xs font-semibold">사고 {bundle.briefs.length}</span>}
          {bundle.court_rulings.length > 0 && <span className="px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-700 dark:text-purple-300 text-xs font-semibold">판례 {bundle.court_rulings.length}</span>}
          {bundle.law_changes.length > 0 && <span className="px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-700 dark:text-blue-300 text-xs font-semibold">법령 {bundle.law_changes.length}</span>}
          {bundle.kosha_guides.length > 0 && <span className="px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 text-xs font-semibold">가이드 {bundle.kosha_guides.length}</span>}
          {bundle.weather_alerts.length > 0 && <span className="px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-300 text-xs font-semibold">날씨 {bundle.weather_alerts.length}</span>}
          {bundle.subsidy_alerts.length > 0 && <span className="px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-700 dark:text-rose-300 text-xs font-semibold">보조 {bundle.subsidy_alerts.length}</span>}
          {isEmpty && <span className="text-xs text-ink-faint">데이터 없음</span>}
        </div>
      </button>

      {/* 본문 — 섹션별 컬러 구분 */}
      {open && (
        <div className="border-t-2 border-surface-elevated/60 px-5 py-5 space-y-6 bg-surface/30">
          {content}
        </div>
      )}
    </Card>
  )
}
